import { Fragment, type ReactNode } from "react";
import CountUp from "./CountUp";

// Minimal inline-Markdown renderer for the content layer.
// Supports exactly what src/content uses: **bold**, *italic*, and [text](url).
// Marks may nest across types (e.g. a link inside **bold**) via recursion, but
// not the same mark within itself. No block syntax (lists/headings).
// If content ever outgrows this, swap it for react-markdown.

// Matches, in priority order: link, bold, italic. Order matters so ** wins over *.
// A fresh RegExp is created per renderInline call — the tokenizer recurses, and a
// shared /g regex carries lastIndex state that recursion would corrupt.
const INLINE_SOURCE = "\\[([^\\]]+)\\]\\(([^)]+)\\)|\\*\\*([^*]+)\\*\\*|\\*([^*]+)\\*";

function isExternal(url: string): boolean {
  return /^https?:\/\//.test(url);
}

/** A bold run that is nothing but a figure, e.g. "$299,734,297" or "43%". */
const FIGURE = /^(\D*?)(\d[\d,]*(?:\.\d+)?)(\D*)$/;

interface InlineOptions {
  /** Render bold figures as a count-up animation. Off unless asked for. */
  animateNumbers?: boolean;
}

/** Turn a bold run into a CountUp if it is purely a figure, else null. */
function figureNode(text: string, key: number): ReactNode | null {
  const match = FIGURE.exec(text.trim());
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const value = Number(digits.replace(/,/g, ""));
  if (!Number.isFinite(value)) return null;

  const dot = digits.indexOf(".");
  return (
    <CountUp
      key={key}
      value={value}
      prefix={prefix}
      suffix={suffix}
      fractionDigits={dot === -1 ? 0 : digits.length - dot - 1}
    />
  );
}

/** Render a single Markdown string as inline React nodes. */
export function renderInline(text: string, options: InlineOptions = {}): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = new RegExp(INLINE_SOURCE, "g");
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const [, linkText, linkHref, boldText, italicText] = match;
    if (linkHref !== undefined) {
      nodes.push(
        isExternal(linkHref) ? (
          <a
            key={key++}
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${linkText} (opens in new tab)`}
          >
            {linkText}
          </a>
        ) : (
          <a key={key++} href={linkHref}>
            {linkText}
          </a>
        )
      );
    } else if (boldText !== undefined) {
      const figure = options.animateNumbers ? figureNode(boldText, key) : null;
      nodes.push(<strong key={key++}>{figure ?? renderInline(boldText, options)}</strong>);
    } else if (italicText !== undefined) {
      nodes.push(<em key={key++}>{renderInline(italicText, options)}</em>);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

interface MarkdownProps {
  /** A single Markdown string. Rendered inline (no wrapping block element). */
  children: string;
  /** Count bold figures up from zero when they scroll into view. */
  animateNumbers?: boolean;
}

/** Inline Markdown, e.g. inside your own <p>, <li>, <summary>, or heading. */
export function Markdown({ children, animateNumbers }: MarkdownProps) {
  return (
    <>
      {renderInline(children, { animateNumbers }).map((node, i) => (
        <Fragment key={i}>{node}</Fragment>
      ))}
    </>
  );
}

interface MarkdownParagraphsProps {
  /** One Markdown string per paragraph. */
  items: string[];
  /** className applied to each <p>. */
  className?: string;
}

/** Render an array of Markdown strings as a sequence of <p> elements. */
export function MarkdownParagraphs({ items, className }: MarkdownParagraphsProps) {
  return (
    <>
      {items.map((text, i) => (
        <p key={i} className={className}>
          <Markdown>{text}</Markdown>
        </p>
      ))}
    </>
  );
}
